import { useEffect, useRef, useLayoutEffect, createRef } from "react"
import styled from "@emotion/styled"
import hash from "@emotion/hash"

import { useConsole } from "contexts/Console"
import { fullGrid } from "css/grid"
import useConstant from "hooks/useConstant"

import { StateProvider, useValue } from "./context"
import FeatureIntro from "./FeatureIntro"
import FeatureErsive from "./FeatureErsive"
import Feature from "./Feature"
import { Bg } from "./commons"
import Pagination from "./Pagination"
import ScrollInvit from "./ScrollInvit"

const Root = styled.section`
  margin-block-start: -100vh;
  z-index: 0;
  ${fullGrid}
`

const compFeature = {
  bezel: Feature,
  dial: Feature,
  material: FeatureErsive,
  bracelet: FeatureErsive,
  function_dual_time: null,
  cyclop: null,
  movement: null,
}

function Features({ intro_v7, features, majesty, comps, name }) {
  const console = useConsole()

  const ref = useRef()
  const refs = useConstant(() => new Map(Array.from(Array(comps.length + 1)).map((item, i) => [i, createRef()])))

  const { currentIndex, targetSlide, globalProg, inputMap, colorSet } = useValue()

  const order = features.map(({ topic_key }) => topic_key)

  function onY(y) {
    const arr = inputMap
      .get()
      .split(",")
      .map(str => Number(str))
    currentIndex.set(arr.findLastIndex(item => item <= y + window.innerHeight / 2))
  }
  useEffect(() => globalProg.onChange(onY))

  function onTargetChange(i) {
    if (i === -1) return
    const arr = inputMap
      .get()
      .split(",")
      .map(str => Number(str))

    const target = arr[i]

    window.scrollTo({
      top: target,
      left: 0,
      behavior: "smooth",
    })
    if (i === 0 || i === arr.length - 2) {
      targetSlide.set(-1)
    }
  }
  useEffect(() => targetSlide.onChange(onTargetChange))

  const sequence = comps.filter(({ topic_key }) => topic_key === "bracelet" || topic_key === "material")

  function onscroll() {
    globalProg.set(window.scrollY - ref.current.offsetTop - ref.current.offsetParent.offsetTop)
  }

  useLayoutEffect(() => {
    const r = ref.current

    const resizeObserver = new ResizeObserver(entries => {
      for (const entry of entries) {
        if (entry.contentBoxSize) {
          const pos = [...refs].reduce((acc, arr) => acc.concat([acc[acc.length - 1] + arr[1].current.offsetHeight]), [0]).sort((a, b) => a - b)
          pos[0] = pos[0] + window.innerHeight * 1
          pos.unshift(0)
          inputMap.set(pos.toString())
        }
      }
    })

    resizeObserver.observe(r)

    return () => resizeObserver.unobserve(r)
  }, [])

  useLayoutEffect(() => {
    window.addEventListener("scroll", onscroll, { passive: true })
    return () => window.removeEventListener("scroll", onscroll, { passive: true })
  }, [])

  return (
    <Root ref={ref} className={`pcs${hash(colorSet)} dark-theme`}>
      <Bg />
      <FeatureIntro ref={refs.get(0)} {...intro_v7} baseline={majesty.baseline} index={1} />
      {comps.map(({ topic_key, ...feat }, i) => {
        const Comp = compFeature[topic_key]
        return !!Comp ? (
          <Comp key={`feature-${i}-${topic_key}`} ref={refs.get(i + 1)} {...feat} topic_key={topic_key} index={i + 2} order={order} sequence={sequence} />
        ) : null
      })}
      <Pagination sections={[intro_v7].concat(comps)} name={name} />
      <ScrollInvit />
    </Root>
  )
}

export default function ModelFeatures({ ...props }) {
  const console = useConsole()

  const comps = props.features.map(({ topic_key, ...feat }, i) => (!!compFeature[topic_key] ? { topic_key, ...feat } : null)).filter(item => !!item)
  const DEFAULT_COLOR_SET = "page-color-set-rolex-v7-color-001"

  return (
    <StateProvider comps={comps} colorSet={props?.page?.color_set || DEFAULT_COLOR_SET}>
      <Features {...props} comps={comps} name={props.name} />
    </StateProvider>
  )
}
